import { useBreadcrumbTitle } from "@msuite/ui/app-shell";
import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { AuthForm } from "@msuite/ui/form";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQuery, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { getPartnerConnectLink } from "@msuite/utils/organizations";
import { guardString } from "@msuite/utils/strings";
import { type FormEvent, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ConnectPartnerConfirmation } from "@/components/partners/connect-partner-confirmation";
import { client } from "@/hono";
import { takeConnectCode } from "@/utils/connect-code-stash";
import { isPhoneBrowser } from "@/utils/is-phone-browser";

/**
 * The page a shared connect link opens: the code's owner is previewed, and confirming makes the
 * viewer's organisation its partner. A code whose owner already hosts the viewer offers the
 * partner's page instead. On a phone the page also offers the native app's deep link.
 */
export function ConnectPartner() {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { code } = useParams<{ code: string }>();
	const navigate = useNavigate();
	const qc = useQueryClient();

	/** State */
	const [isSubmitting, setIsSubmitting] = useState(false);

	/** Query */
	const { data: preview, isLoading } = useQuery({
		retry: false,
		queryKey: ["organizationPartnerships", "connect", code ?? null],
		queryFn: async () => {
			const response = await client.organizations.connect[":code"].$get({
				param: { code: guardString(code, "") },
			});
			const data = await responseHandler(response);
			return { organization: data.organization, partnership: data.partnership };
		},
		enabled: Boolean(code),
	});

	/** Effects */
	useBreadcrumbTitle(code, preview?.organization.name);

	/** Functions */
	async function handleOnSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!preview || !code) return;
		if (preview.partnership) {
			handleOnOpenPartner(preview.organization.id);
			return;
		}
		setIsSubmitting(true);
		try {
			const response = await client.organizations.connect.$post({ json: { code } });
			const data = await responseHandler(response);
			await qc.invalidateQueries({ predicate: invalidate(["organizationPartnerships"]) });
			toast.success(t("organization.connected_organization_successfully"));
			handleOnOpenPartner(data.partnership.hostOrgId);
		} finally {
			setIsSubmitting(false);
		}
	}

	function handleOnOpenPartner(hostOrgId: string) {
		takeConnectCode();
		navigate(`/partners/${hostOrgId}`);
	}

	/** Computed */
	const appLink = code && isPhoneBrowser() ? getPartnerConnectLink(code) : null;

	/** Guard */
	if (!isLoading && !preview) {
		return (
			<div className="flex-center w-full h-full">
				<BlankSlate
					Icon={icon("Unlink")}
					label={t("general.something_went_wrong")}
				/>
			</div>
		);
	}

	/** Render */
	return (
		<div className="flex-center w-full h-full flex-col gap-2">
			<AuthForm
				onSubmit={handleOnSubmit}
				data-cy="connect-partner-form"
			>
				<AuthForm.Header>
					<h1 className="heading">{t("organization.connect_confirmation_title")}</h1>
				</AuthForm.Header>
				<AuthForm.Body>
					{preview ? (
						<ConnectPartnerConfirmation
							organization={preview.organization}
							partnership={preview.partnership}
						/>
					) : (
						<BlankSlate isLoading />
					)}
				</AuthForm.Body>
				<AuthForm.Footer>
					<Button
						size="sm"
						variant="primary"
						type="submit"
						isLoading={isSubmitting}
						isDisabled={!preview}
					>
						{preview?.partnership
							? t("organization.open_partner")
							: t("organization.connect_organization")}
					</Button>
					<Button
						size="sm"
						variant="ghost"
						onClick={() => navigate("/")}
					>
						{t("general.cancel")}
					</Button>
					{appLink && (
						<a
							href={appLink}
							className="link text-center"
						>
							{t("organization.open_in_app")}
						</a>
					)}
				</AuthForm.Footer>
			</AuthForm>
		</div>
	);
}
